/**
 * Tiny typed event bus for fire-and-forget UI ↔ renderer signals that are not state
 * (camera requests, viewport reports, one-shot effects). State belongs in the stores.
 */
import type { HexId } from '@friction/engine';

export interface BusEvents {
  /** Ask the renderer to centre the camera on a hex (optionally at a zoom level). */
  focusHex: { hex: HexId; zoom?: number };
  /** Renderer reports the visible world rectangle (for the minimap). */
  camera: { x: number; y: number; width: number; height: number; zoom: number };
  /** Ask the renderer to pan to a world position (minimap click). */
  panTo: { x: number; y: number };
  /** Play a UI sound (audio module subscribes). */
  sound: { name: string; volume?: number };
  /** Renderer: lightning struck at a world point (the audio answers with thunder). */
  lightning: { x: number; y: number; strength: number };
}

type Handler<T> = (payload: T) => void;
const handlers = new Map<keyof BusEvents, Set<Handler<never>>>();

export const bus = {
  on<K extends keyof BusEvents>(event: K, fn: Handler<BusEvents[K]>): () => void {
    let set = handlers.get(event);
    if (!set) {
      set = new Set();
      handlers.set(event, set);
    }
    set.add(fn as Handler<never>);
    return () => set.delete(fn as Handler<never>);
  },
  emit<K extends keyof BusEvents>(event: K, payload: BusEvents[K]): void {
    const set = handlers.get(event);
    if (set) for (const fn of set) (fn as Handler<BusEvents[K]>)(payload);
  },
};
